import { captureError, addBreadcrumb } from '../config/sentry';
import { fetchWithTokenRefresh } from './tokenRefresh';
import { AURA_CLIENT_HEADERS } from './auraClient';

const getBackendBaseUrl = () => {
  const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL;
  if (configuredBackendUrl) {
    return configuredBackendUrl.replace(/\/+$/, '');
  }

  const configuredApiUrl = process.env.REACT_APP_API_URL;
  if (configuredApiUrl) {
    return configuredApiUrl
      .replace(/\/api\/v1\/?$/, '')
      .replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin?.startsWith('http')) {
    return window.location.origin.replace(/\/+$/, '');
  }

  return '';
};

/**
 * Absolute API-URL fuer rohe fetch()-Aufrufe. Im Browser ist ein relativer
 * Pfad wie /api/v1/... ok, in der nativen App (Capacitor, https://localhost)
 * landet er aber im lokalen Asset-Server statt beim Backend.
 */
export const apiUrl = (path) => `${getBackendBaseUrl()}${path}`;

/**
 * Macht aus relativen Pfaden (/uploads/...) absolute URLs mit Backend-Domain.
 * Im Browser unnoetig (relative Pfade funktionieren), aber in der nativen App
 * (Capacitor) zeigen relative Pfade auf localhost statt auf den Server.
 */
export const resolveAssetUrl = (path) => {
  if (!path || typeof path !== 'string') return '';

  const cleanPath = path.trim();
  if (!cleanPath) return '';

  // Bereits absolute oder lokale Browser-URL (nicht aendern)
  if (
    cleanPath.startsWith('http://') ||
    cleanPath.startsWith('https://') ||
    cleanPath.startsWith('data:') ||
    cleanPath.startsWith('blob:') ||
    cleanPath.startsWith('file://') ||
    cleanPath.startsWith('content://') ||
    cleanPath.startsWith('capacitor://')
  ) {
    return cleanPath;
  }

  if (cleanPath.startsWith('//')) {
    return `https:${cleanPath}`;
  }

  const baseUrl = getBackendBaseUrl();
  if (!baseUrl) return cleanPath;

  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Liefert die kleine WebP-Variante eines Avatars für Listen-Ansichten.
 * 'thumb' (192px) für Listen-Avatare (Anzeige <= 96px), 'card' (384px) für die
 * grosse Match-Karte. Andere Pfade (Logos, absolute URLs) bleiben unverändert;
 * generierte Platzhalter (data:-SVG, ui-avatars) ergeben '' -> Marken-Platzhalter.
 * Backend-Gegenstück: backend/src/utils/avatarVariants.js (Lazy-Generierung für alte Avatare).
 *
 * IDEMPOTENT: ein bereits aufgelöster Variantenpfad wird nicht erneut umgeschrieben.
 * Ohne diese Sperre würde ein zweiter Aufruf 'thumb-thumb-<datei>.webp' erzeugen (404).
 * Relevant, seit ui/Avatar.jsx intern selbst auflöst und Aufrufer teils schon
 * aufgelöste Pfade übergeben. Originale heißen backend-seitig 'processed-*',
 * nie 'thumb-*'/'card-*' — das Präfix ist also ein sicheres Erkennungsmerkmal.
 */
const AVATAR_VARIANT_PATTERN = /^(thumb|card)-.+\.webp$/;

/**
 * Erkennt maschinell erzeugte Platzhalter-"Bilder" aus frueheren Fassungen:
 * das Backend schrieb bis Sep 2026 bei CSV-Import und SSO-Login ein SVG mit
 * Initialen (Arial, Zufallsfarbe) als data:-URI ins Profil, die Rangliste
 * lieferte ui-avatars.com-Links. Beide sahen anders aus als der Marken-
 * Platzhalter (Bungee-Initialen in ui/Avatar.jsx) - deshalb hatten Nutzer
 * ohne Foto je nach Herkunft zwei verschiedene Kreise. Solche Werte gelten
 * hier als "kein Bild", damit ueberall derselbe Platzhalter gezeichnet wird.
 * Echte Uploads liegen immer unter /uploads/avatars/ (WebP), nie als data:-SVG.
 */
export const isGeneratedPlaceholderAvatar = (path) => {
  if (!path || typeof path !== 'string') return false;
  const p = path.trim();
  return p.startsWith('data:image/svg+xml') || p.includes('ui-avatars.com/');
};

export const resolveAvatarUrl = (path, variant = 'thumb') => {
  if (!path || typeof path !== 'string') return '';
  if (isGeneratedPlaceholderAvatar(path)) return '';

  const cleanPath = path.trim();
  const avatarsPrefix = '/uploads/avatars/';
  if (!cleanPath.startsWith(avatarsPrefix)) return resolveAssetUrl(cleanPath);

  const fileName = cleanPath.slice(avatarsPrefix.length);
  if (!fileName || fileName.includes('/')) return resolveAssetUrl(cleanPath);
  if (AVATAR_VARIANT_PATTERN.test(fileName)) return resolveAssetUrl(cleanPath);

  const base = fileName.replace(/\.[^.]+$/, '');
  return resolveAssetUrl(`${avatarsPrefix}${variant}-${base}.webp`);
};

export const parseJsonResponse = async (response, options = {}) => {
  const { allowPathFallback = false } = options;
  const responseText = await response.text();

  if (!responseText) return {};

  try {
    return JSON.parse(responseText);
  } catch (error) {
    const trimmed = responseText.trim();

    if (
      allowPathFallback &&
      (trimmed.startsWith('/uploads/') ||
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://'))
    ) {
      return {
        url: trimmed,
        avatar: trimmed,
        avatarUrl: trimmed,
        logo: trimmed
      };
    }

    const parseError = new Error(`Ungueltige Server-Antwort (${response.status})`);
    parseError.name = 'ResponseParseError';
    parseError.status = response.status;
    parseError.rawResponse = trimmed.slice(0, 300);
    throw parseError;
  }
};

/**
 * Basis-API-Funktion ohne Token-Abhängigkeiten (für AuthContext)
 */
export const apiFetch = async (url, options = {}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002';
  const fullUrl = `${baseUrl}${url}`;

  return fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...AURA_CLIENT_HEADERS,
      ...options.headers,
    },
  });
};

/**
 * API-Funktion mit manueller Token-Verwaltung (für AuthContext)
 *
 * Bei 401 einmal Token erneuern und wiederholen, wie authorizedFetch. Ohne das
 * warf getUser (Level-Up, Session beenden) nach dem Token-Ablauf hart ab, obwohl
 * /auth/refresh das Token noch 7 Tage lang erneuert.
 */
export const apiFetchWithToken = async (url, token, options = {}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002';
  const fullUrl = `${baseUrl}${url}`;

  const doRequest = (bearerToken) => fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...AURA_CLIENT_HEADERS,
      'Authorization': `Bearer ${bearerToken}`,
      ...options.headers,
    },
  });

  return fetchWithTokenRefresh(doRequest, token, url);
};

/**
 * Hilfsfunktion für authentifizierte API-Anfragen
 */
export const authorizedFetch = async (url, options = {}) => {
  // Token aus dem localStorage abrufen
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const schoolId = user.schoolId || localStorage.getItem('schoolId');

  if (!token) {
    throw new Error('Nicht authentifiziert - kein Token verfügbar');
  }

  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002';
  const fullUrl = `${baseUrl}${url}`;

  // Headers zusammenbauen
  const headers = {
    ...AURA_CLIENT_HEADERS,
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  // Content-Type: application/json automatisch setzen, wenn ein String-Body
  // (also JSON.stringify) gesendet wird und noch kein Content-Type gesetzt ist.
  // Ohne diesen Header ignoriert der Express-Body-Parser den Inhalt komplett
  // (req.body bleibt leer). FormData-Bodys bleiben unberuehrt, damit der Browser
  // dort den multipart-Content-Type mit boundary selbst setzen kann.
  const hasContentType = Object.keys(headers).some(
    (h) => h.toLowerCase() === 'content-type'
  );
  if (!hasContentType && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  // SchoolId zum Header hinzufügen, wenn verfügbar
  if (schoolId) {
    headers['X-School-ID'] = schoolId;
  }

  // UserId zum Header hinzufügen, wenn verfügbar
  if (user && user.id) {
    headers['X-User-ID'] = user.id;
  }

  // Bei 401 einmal Token erneuern und wiederholen (siehe utils/tokenRefresh.js).
  const doRequest = (bearerToken) => fetch(fullUrl, {
    ...options,
    headers: { ...headers, 'Authorization': `Bearer ${bearerToken}` },
    credentials: 'include'
  });

  return fetchWithTokenRefresh(doRequest, token, url);
};

/**
 * JSON-Helfer für API-Calls
 */
export const apiCall = async (url, options = {}) => {
  // Sentry Breadcrumb: API-Call wird versucht
  addBreadcrumb(`API Call: ${options.method || 'GET'} ${url}`, 'http', 'info', {
    url,
    method: options.method || 'GET'
  });

  try {
    const response = await authorizedFetch(url, options);

    if (!response.ok) {
      // Versuche zunächst die JSON-Antwort zu lesen
      let errorData;
      try {
        errorData = await response.json();
      } catch (jsonError) {
        // Wenn keine gültige JSON-Antwort, verwende Text oder Status
        errorData = {
          error: `HTTP Error ${response.status}: ${response.statusText}`,
          status: response.status
        };
      }

      // Erstelle einen strukturierten Fehler.
      // Bei Konto-Sperrung (Anti-Mobbing) liefert die Tenant-Middleware die
      // eigentliche Meldung in `details` — die soll der Nutzer sehen, nicht
      // nur das generische "Zugriff verweigert".
      const suspensionMessage = response.status === 403
        && typeof errorData.details === 'string'
        && errorData.details.includes('gesperrt')
        ? errorData.details
        : null;

      // Backend-Fehler koennen als String ODER als strukturiertes Objekt
      // ankommen (z.B. { error: { code, message, details } } bei Auth-401).
      // Ein Objekt wuerde als "[object Object]" in der Sentry-Exception und im
      // Nutzer-Toast landen (Sentry-Issue JAVASCRIPT-REACT-11). Deshalb auf
      // einen lesbaren String normalisieren und den Fehlercode separat halten.
      const rawError = errorData.error ?? errorData.message;
      const structuredError = rawError && typeof rawError === 'object' ? rawError : null;
      const readableMessage = structuredError
        ? (structuredError.message || structuredError.details || structuredError.code || `Fehler: ${response.status}`)
        : (rawError || `Fehler: ${response.status}`);

      // Echtes Error-Objekt mit message/code/status/details/url statt eines
      // Plain-Objects: Die Aufrufer loggen den Fehler meist noch einmal per
      // console.error(..., error), und ein Plain-Object kam in Sentry nur als
      // "[object keys: message,code,status,details,url]" an — unlesbar und am
      // Noise-Filter vorbei. message bleibt die lesbare Meldung, die Aufrufer anzeigen.
      const apiError = new Error(suspensionMessage || readableMessage);
      apiError.name = 'APIError';
      apiError.code = structuredError?.code || errorData.code || null;
      apiError.status = response.status;
      apiError.details = structuredError?.details || errorData.details || null;
      // Zusatzfelder einer Fehlerantwort (z. B. chatId bei ALREADY_CONNECTED, #492)
      apiError.body = errorData;
      apiError.url = url;

      // 🚨 SENTRY: API-Fehler protokollieren. Gemeldet und geworfen wird DASSELBE
      // Objekt — das Sentry-SDK erkennt es als bereits erfasst und schickt es bei
      // späteren console.error-Aufrufen kein zweites Mal.
      captureError(apiError, {
        apiRequest: {
          url,
          method: options.method || 'GET',
          statusCode: response.status
        },
        errorResponse: errorData
      });

      console.error('API-Fehler:', apiError);
      throw apiError;
    }
    
    // Prüfe, ob die Antwort JSON ist
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // Bei Nicht-JSON-Antworten gib ein Objekt mit Textinhalt zurück
      const text = await response.text();
      return { 
        success: response.ok,
        text,
        status: response.status
      };
    }
  } catch (error) {
    // Bereits strukturierter Fehler von oben - nicht erneut loggen
    if (error.status && error.message) {
      throw error;
    }

    // Andere Fehler (Netzwerkfehler, etc.) — ebenfalls als echtes Error-Objekt,
    // gemeldet und geworfen als dasselbe Objekt (siehe oben).
    const networkError = new Error(error.message || 'Netzwerkfehler');
    networkError.name = 'NetworkError';
    networkError.status = 0;
    networkError.details = 'Verbindungsfehler zum API-Server';
    networkError.url = url;

    // 🚨 SENTRY: Netzwerkfehler protokollieren
    captureError(networkError, {
      apiRequest: {
        url,
        method: options.method || 'GET'
      },
      originalError: {
        message: error.message,
        stack: error.stack
      }
    });

    console.error('API-Fehler:', networkError);
    throw networkError;
  }
}; 
